<script>
import { defineComponent } from 'vue'
import { ElMessage, ElMessageBox, ElNotification } from 'element-plus'
import StateBudgetExecutionReportForm from './StateBudgetExecutionReportForm.vue'

export default defineComponent({
  name: 'StateBudgetExecutionReportList',
  components: { StateBudgetExecutionReportForm },
  data() {
    return {
      loading: false,
      tableData: [],
      page: 1,
      limit: 20,
      totalCount: 0,
      filterYear: null,
      filterPeriodType: '',
      filterQuarter: null,
    }
  },
  computed: {
    years() {
      const current = new Date().getFullYear()
      const list = []
      for (let y = current; y >= current - 10; y--) list.push(y)
      return list
    },
    showQuarterFilter() {
      return this.filterPeriodType === 'QUARTERLY'
    },
    baseFileUrl() {
      return process.env.VUE_APP_BASE_API_URL + 'api/v2/info/file/'
    },
  },
  methods: {
    indexMethod(index) {
      return index + (this.page - 1) * this.limit + 1
    },
    handleCurrentChange() {
      this.getList()
    },
    handleSizeChange() {
      this.page = 1
      this.getList()
    },
    onFilterChange() {
      if (this.filterPeriodType !== 'QUARTERLY') this.filterQuarter = null
      this.page = 1
      this.getList()
    },
    onDelete(id) {
      ElMessageBox.confirm("Ma'lumotni o'chirasizmi?", 'Diqqat!', {
        confirmButtonText: 'Ha',
        cancelButtonText: 'Bekor qilish',
        type: 'warning',
      })
        .then(() => {
          this.loading = true
          this.$http
            .delete(`/api/v1/state-budget-execution-reports/${id}`)
            .then(() => {
              ElMessage({ type: 'success', message: "Muvaffaqiyatli o'chirildi" })
              this.getList()
            })
            .catch(() => {
              ElNotification({ type: 'error', title: 'Xatolik', message: "Iltimos, qayta urinib ko'ring" })
            })
            .finally(() => { this.loading = false })
        })
        .catch(() => {
          ElMessage({ type: 'info', message: "O'chirish bekor qilindi" })
        })
    },
    getList() {
      this.loading = true
      this.$http
        .get('api/v1/state-budget-execution-reports', {
          params: {
            limit: this.limit,
            offset: this.limit * (this.page - 1),
            year: this.filterYear || undefined,
            period_type: this.filterPeriodType || undefined,
            quarter: this.filterQuarter || undefined,
          },
        })
        .then(({ data }) => {
          this.tableData = data.data
          this.totalCount = data.count
        })
        .catch(({ response }) => {
          ElNotification.error({ message: response?.data?.message || "Ma'lumot yuklashda xatolik sodir bo'ldi" })
        })
        .finally(() => { this.loading = false })
    },
    periodTypeLabel(type) {
      return type === 'YEARLY' ? 'Yillik' : 'Choraklik'
    },
  },
  mounted() {
    this.getList()
  },
})
</script>

<template>
  <div v-loading.fullscreen="loading">
    <div class="header">
      <div class="filters">
        <el-select
          v-model="filterPeriodType"
          placeholder="Davr turi"
          clearable
          style="width: 160px"
          @change="onFilterChange"
        >
          <el-option label="Yillik" value="YEARLY" />
          <el-option label="Choraklik" value="QUARTERLY" />
        </el-select>

        <el-select
          v-model="filterYear"
          placeholder="Yil"
          clearable
          style="width: 120px"
          @change="onFilterChange"
        >
          <el-option v-for="y in years" :key="y" :label="y" :value="y" />
        </el-select>

        <el-select
          v-if="showQuarterFilter"
          v-model="filterQuarter"
          placeholder="Chorak"
          clearable
          style="width: 140px"
          @change="onFilterChange"
        >
          <el-option v-for="q in [1, 2, 3, 4]" :key="q" :label="`${q}-chorak`" :value="q" />
        </el-select>
      </div>

      <state-budget-execution-report-form @getData="getList" />
    </div>

    <el-table :data="tableData" class="table" border height="calc(100vh - 230px)">
      <el-table-column type="index" label="T/R" width="70" :index="indexMethod" align="center" />
      <el-table-column label="Muqova" width="90" align="center">
        <template #default="scope">
          <img
            v-if="scope.row.cover_photo_id"
            :src="baseFileUrl + scope.row.cover_photo_id"
            alt="cover"
            class="cover-thumb"
          />
          <span v-else>—</span>
        </template>
      </el-table-column>
      <el-table-column label="Sarlavha" prop="title" min-width="240" show-overflow-tooltip />
      <el-table-column label="Davr turi" width="130" align="center">
        <template #default="scope">
          <el-tag :type="scope.row.period_type === 'YEARLY' ? 'success' : 'primary'" size="small">
            {{ periodTypeLabel(scope.row.period_type) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Yil" prop="year" width="80" align="center" />
      <el-table-column label="Chorak" width="90" align="center">
        <template #default="scope">
          {{ scope.row.quarter ? `${scope.row.quarter}-chorak` : '—' }}
        </template>
      </el-table-column>
      <el-table-column label="Qo'shilgan sana" width="160" align="center">
        <template #default="scope">
          {{ scope.row.created_at ? new Date(scope.row.created_at).toLocaleDateString('ru-RU') : '—' }}
        </template>
      </el-table-column>
      <el-table-column label="" align="center" width="200">
        <template #default="scope">
          <state-budget-execution-report-form :id="scope.row.id" @getData="getList" />
          <el-button style="margin-left: 8px" type="danger" size="small" @click="onDelete(scope.row.id)">
            O'chirish
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination">
      <div>Umumiy soni: {{ totalCount }}</div>
      <el-pagination
        background
        layout="sizes, prev, pager, next"
        :total="totalCount"
        v-model:page-size="limit"
        v-model:current-page="page"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<style scoped>
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 10px;
}
.filters {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}
.cover-thumb {
  width: 56px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
}
</style>
